import { Injectable, inject } from "@angular/core";
import { Ref } from "../../../../shared/providers/ref";
import { NowcastingState, NowcastingTab } from "./nowcasting-state";
import {
  NowcastingReportEntity,
  NowcastingItemEntity,
  ResponseValueObject,
} from "@gishub/domain";
import { SenamhiRepositoryProvider } from "../../../../shared/providers/repository-providers";
import { MapProvider } from "../map/map-provider";
import { FooterPanelProvider } from "../footer-panel/footer-panel-provider";
import { MenuProvider } from "../../shared/menu/menu-provider";
import {
  SenamhiLayerNotifier,
  SenamhiLayerItem,
  LEVEL_CONFIGS,
} from "../senamhi/senamhi-layer-notifier";

type LayerType = "current" | "forecast_1h" | "forecast_2h" | "forecast_24h";

interface NowcastingLayerConfig {
  type: LayerType;
  label: string;
}

export const NOWCASTING_LAYER_CONFIGS: NowcastingLayerConfig[] = [
  { type: "current", label: "Tiempo Actual" },
  { type: "forecast_1h", label: "Pronóstico 1 h" },
  { type: "forecast_2h", label: "Pronóstico 2 h" },
  { type: "forecast_24h", label: "Pronóstico 24 h" },
];

export { LEVEL_CONFIGS };

@Injectable()
export class NowcastingNotifier extends SenamhiLayerNotifier<NowcastingState> {
  private static readonly INITIAL: NowcastingState = {
    loading: false,
    error: null,
    report: null,
    activeTab: "current",
    visibleItems: new Set<string>(),
    activeLayerCount: 0,
    layerOpacities: {},
    loadedAt: null,
    emittedAt: null,
    refreshEpoch: 0,
    syncing: false,
    hiddenLevels: [],
  };

  private _loaded = false;

  /** Fuente en uso (menú nowcasting abierto). Puerta del auto-refresh. */
  private _open = false;

  constructor(ref: Ref) {
    super(ref, NowcastingNotifier.INITIAL);
  }

  // ── Hooks de capa SENAMHI ─────────────────────────────────────

  override build(): void {
    super.build();

    // Reactivo: abrir/cerrar footer panel según estado del menú nowcasting
    let wasOpen = false;
    this.listen(() => {
      const activeId = this.ref.read(MenuProvider).state().activeId;
      const isOpen = activeId === "nowcasting";
      const footerPanel = this.ref.read(FooterPanelProvider);

      if (!wasOpen && isOpen) {
        // Se abrió nowcasting → abrir footer panel (el componente registra su
        // template) y, si el dato ya está viejo, refrescar de inmediato.
        this._open = true;
        if (!this._loaded) this.ensureLoaded();
        else this.checkAndRefreshNow();
        footerPanel.notifier.open();
      } else if (wasOpen && !isOpen) {
        // Se cerró nowcasting → cerrar footer panel y limpiar capas
        this._open = false;
        this.removeAllLayers();
        footerPanel.notifier.setContent([], "");
        footerPanel.notifier.close();
      }
      wasOpen = isOpen;
    });
  }

  protected getCql(item: SenamhiLayerItem): string {
    // current/1h/2h: nivel>=2 (numérico) | 24h: nivel>='Nivel 2' (string)
    const isNowcasting = item.downloadUrl.includes("g_nowcasting");
    const is24h = item.downloadUrl.includes("g_prono_pp_24h");
    return isNowcasting
      ? "&CQL_FILTER=nivel%3E%3D2"
      : is24h
        ? "&CQL_FILTER=nivel%3E%3D%27Nivel%202%27"
        : "";
  }

  protected resolveItem(key: string): SenamhiLayerItem | null {
    const idx = key.lastIndexOf("-");
    if (idx < 0) return null;
    const type = key.slice(0, idx) as LayerType;
    const index = Number(key.slice(idx + 1));
    return this.getItems(type)[index] ?? null;
  }

  protected buildLayerId(key: string): string {
    return `nowcasting-${key}`;
  }

  protected buildLayerTitle(key: string): string {
    const idx = key.lastIndexOf("-");
    const type = key.slice(0, idx) as LayerType;
    const index = Number(key.slice(idx + 1));
    const config = NOWCASTING_LAYER_CONFIGS.find((c) => c.type === type);
    const item = this.getItems(type)[index];
    return `Nowcasting – ${config?.label ?? type} – ${item?.analysisDatetime ?? ""}`;
  }

  protected buildFields(): any[] {
    return [
      { name: "OBJECTID", type: "oid" },
      { name: "nivel", type: "string" },
      { name: "ppmin", type: "double" },
      { name: "ppmax", type: "double" },
      { name: "producto", type: "string" },
      { name: "fichero", type: "string" },
      { name: "fecha1", type: "string" },
      { name: "fecha2", type: "string" },
      { name: "descripcio", type: "string" },
      { name: "recomendac", type: "string" },
    ];
  }

  // ── Lifecycle ────────────────────────────────────────────────

  ensureLoaded(): void {
    if (this._loaded) return;
    this._loaded = true;
    this.loadReport();
  }

  // ── Tab ───────────────────────────────────────────────────────

  async setActiveTab(tab: NowcastingTab): Promise<void> {
    const prev = this.snapshot().activeTab;
    if (prev === tab) return;
    this.hideAllLayers();
    this.updateState((s) => ({ ...s, activeTab: tab }));
    const items = this.getItems(tab);
    if (items.length > 0) await this.selectLayer(`${tab}-0`);
  }

  // ── Data ──────────────────────────────────────────────────────

  /**
   * Carga el reporte. Con `addLayers:false` NO dibuja la capa "Ahora":
   * permite conocer su nivel real sin seleccionar la fuente todavía (evita
   * el parpadeo y un cruce KPI de más al decidir la fuente inicial).
   */
  async loadReport(options: { addLayers?: boolean } = {}): Promise<void> {
    this.updateState((s) => ({ ...s, loading: true, error: null }));
    this._lastSyncAt = Date.now();
    try {
      const repo = this.ref.watch(SenamhiRepositoryProvider).instance;
      const res: ResponseValueObject<NowcastingReportEntity> =
        await repo.getNowcastingReport();
      if (res.success && res.data) {
        this._loaded = true;
        const first = res.data!.currents[0] ?? null;
        this.updateState((s) => ({
          ...s,
          report: res.data!,
          loading: false,
          syncing: false,
          loadedAt: Date.now(),
          emittedAt: first?.analysisDatetime ?? s.emittedAt,
        }));
        if (options.addLayers !== false) {
          await this.autoAddMostRecentLayers(res.data!);
        }
      } else {
        this.updateState((s) => ({
          ...s,
          error: res.message ?? "Sin datos",
          loading: false,
          syncing: false,
        }));
      }
    } catch (err: any) {
      this.updateState((s) => ({
        ...s,
        error: err?.message ?? "Error al cargar nowcasting",
        loading: false,
        syncing: false,
      }));
    }
  }

  // ── Auto-refresh silencioso (R1) ─────────────────────────────

  protected shouldAutoRefresh(): boolean {
    // Solo cuando la fuente está en uso: menú nowcasting abierto o capas
    // de alerta visibles en el mapa (p. ej. el Centro de decisión).
    return (
      this._loaded &&
      (this._open || this.snapshot().activeLayerCount > 0)
    );
  }

  protected async autoRefresh(): Promise<void> {
    this.updateState((s) => ({ ...s, syncing: true }));
    this._lastSyncAt = Date.now();
    try {
      const repo = this.ref.watch(SenamhiRepositoryProvider).instance;
      const res: ResponseValueObject<NowcastingReportEntity> =
        await repo.getNowcastingReport();
      if (res.success && res.data) {
        const first = res.data!.currents[0] ?? null;
        this.updateState((s) => ({
          ...s,
          report: res.data!,
          loadedAt: Date.now(),
          emittedAt: first?.analysisDatetime ?? s.emittedAt,
        }));
        // Selección viva: si el ítem visible ya no existe en el reporte
        // nuevo, se baja al primero del tab activo.
        await this.ensureFreshSelection();
        await this.syncLayersAndIncrementEpoch();
      } else {
        // Silencioso: conservar los datos previos y reintentar en el tick.
        this.updateState((s) => ({ ...s, syncing: false }));
      }
    } catch {
      this.updateState((s) => ({ ...s, syncing: false }));
    }
  }

  /** Asegura que la selección visible siga resolviendo tras re-emitir. */
  private async ensureFreshSelection(): Promise<void> {
    const tab = this.snapshot().activeTab;
    const items = this.getItems(tab);
    if (items.length === 0) return;
    const visible = [...this.snapshot().visibleItems];
    const valid = visible.filter((k) => this.resolveItem(k) != null);
    if (valid.length > 0) return;
    await this.selectLayer(`${tab}-0`);
  }

  // ── Narrativa del horizonte activo ────────────────────────────

  /**
   * Narrativa oficial del horizonte activo. Para el Aviso 24h la
   * narrativa real viaja en los atributos WFS de sus polígonos
   * (descripcio / recomendac); para los demás horizontes, en las
   * descripciones del reporte.
   */
  get activeNarrative(): string {
    return this.activeNarrativeFor(
      (this.snapshot().activeTab ?? "current") as NowcastingTab,
    );
  }

  /**
   * Narrativa oficial del horizonte `tab`. Para el Aviso 24h la
   * narrativa real viaja en los atributos WFS de sus polígonos
   * (descripcio / recomendac); para los demás, en las descripciones
   * del reporte.
   */
  activeNarrativeFor(tab: NowcastingTab): string {
    // Aviso 24h: el texto oficial vive en los features del mapa.
    if (tab === "forecast_24h") {
      // El índice activo es la capa visible del horizonte ("forecast_24h-0").
      const prefix = "forecast_24h-";
      let idx = -1;
      for (const key of this.snapshot().visibleItems) {
        if (key.startsWith(prefix)) {
          idx = Number(key.slice(prefix.length));
          break;
        }
      }
      const attrs = idx >= 0 ? this.getLayerAttributes(`${prefix}${idx}`) : [];
      const pick = (a: Record<string, unknown>, k: string): string => {
        const v = a[k];
        return typeof v === "string" && v.trim() ? v.trim() : "";
      };
      const first = attrs.find((a) => pick(a, "descripcio")) ?? attrs[0];
      if (first) {
        const desc = pick(first, "descripcio");
        const rec = pick(first, "recomendac");
        const text = [desc, rec].filter(Boolean).join(" ");
        if (text) return text;
      }
    }

    // Respaldo: descripciones del reporte por horizonte.
    const report = this.snapshot().report;
    if (!report) return "";
    switch (tab) {
      case "forecast_1h":
        return report.descriptionForecast1h;
      case "forecast_2h":
        return report.descriptionForecast2h;
      case "forecast_24h":
        return report.descriptionForecast24h ?? "";
      default:
        return report.descriptionCurrent;
    }
  }

  /**
   * Nivel máximo (2–4) de la capa visible del horizonte `tab`, según
   * los features ya cargados. null si aún no hay features.
   */
  maxLevelForTab(tab: NowcastingTab): number | null {
    const prefix = `${tab}-`;
    for (const key of this.snapshot().visibleItems) {
      if (!key.startsWith(prefix)) continue;
      const feats = this.getFeaturesForLayer(key);
      if (feats?.length) {
        return Math.max(...feats.map((f) => f.level));
      }
    }
    return null;
  }

  /** Nivel máximo del horizonte activo (para badges). */
  get activeMaxLevel(): number | null {
    return this.maxLevelForTab(
      (this.snapshot().activeTab ?? "current") as NowcastingTab,
    );
  }

  /**
   * Nivel máximo REAL del horizonte "Ahora" SIN dibujar la capa ni cambiar
   * la selección: consulta el mismo WFS y lee el atributo `nivel`. Permite
   * decidir la fuente inicial por criticidad antes de tocar el mapa/KPI.
   * Devuelve null si no hay ítems o si ningún polígono alcanza el nivel 2.
   */
  async probeCurrentMaxLevel(): Promise<number | null> {
    const item = this.getItems("current")[0];
    if (!item) return null;
    try {
      const proxyBase = this.proxyService.proxyUrl("").replace(/url=$/, "");
      const url = `${proxyBase}${item.downloadUrl}${this.getCql(item)}`;
      const response = await fetch(url);
      if (!response.ok) return null;
      const geojson: any = await response.json();
      let max: number | null = null;
      for (const f of geojson?.features ?? []) {
        const raw = f.properties?.nivel;
        const level =
          typeof raw === "number"
            ? raw
            : parseInt(String(raw).match(/\d+/)?.[0] ?? "0", 10);
        if (level >= 2 && (max === null || level > max)) max = level;
      }
      return max;
    } catch {
      return null;
    }
  }

  // ── Items ─────────────────────────────────────────────────────

  getItems(type: LayerType): NowcastingItemEntity[] {
    const report = this.snapshot().report;
    if (!report) return [];
    switch (type) {
      case "current":
        return [...report.currents];
      case "forecast_1h":
        return [...report.forecasts1h];
      case "forecast_2h":
        return [...report.forecasts2h];
      case "forecast_24h":
        return [...report.forecasts24];
    }
  }

  isItemVisible(type: LayerType, index: number): boolean {
    return this.isLayerVisible(`${type}-${index}`);
  }

  getItemOpacity(type: LayerType, index: number): number {
    return this.getLayerOpacity(`${type}-${index}`);
  }

  setLayerOpacity(type: LayerType, index: number, opacity: number): void {
    this.applyLayerOpacity(`${type}-${index}`, opacity);
  }

  async selectItemLayer(type: LayerType, index: number): Promise<boolean> {
    return this.selectLayer(`${type}-${index}`);
  }

  // ── Auto-add most recent ──────────────────────────────────────

  private async autoAddMostRecentLayers(
    report: NowcastingReportEntity,
  ): Promise<void> {
    const tab = this.snapshot().activeTab;
    const items = this.getItems(tab);
    if (items.length > 0) {
      const key = `${tab}-0`;
      const next = new Set(this.snapshot().visibleItems);
      if (!next.has(key)) {
        next.add(key);
        await this.selectLayer(key);
      }
    }
  }

  formatDatetime(iso: string): string {
    try {
      const d = new Date(iso);
      const months = [
        "ene",
        "feb",
        "mar",
        "abr",
        "may",
        "jun",
        "jul",
        "ago",
        "sep",
        "oct",
        "nov",
        "dic",
      ];
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch {
      return iso;
    }
  }
}
